export type {
  LiveCandleTrade,
  LiveTradeCandle,
  LiveTradeCandleArgs,
} from './live-trade-candles.js';
export { aggregateLiveTradeCandles, LIVE_CANDLE_BUCKET_SECONDS } from './live-trade-candles.js';

export type MarketChartGapPolicy = 'fill-closed' | 'preserve-gaps';

export interface MarketChartLineRow {
  readonly bucket: string;
  readonly sampleTs?: string;
  readonly value: string;
}

export interface MarketChartCandleRow {
  readonly bucket: string;
  readonly open: string;
  readonly high: string;
  readonly low: string;
  readonly close: string;
}

export interface MarketChartPresentationBounds {
  readonly rangeStart: string;
  readonly rangeEnd: string;
  readonly bucketSeconds: number;
  readonly leftEdgeValue?: string;
  readonly closedBefore: string;
  readonly gapPolicy: MarketChartGapPolicy;
}

export type MarketChartLinePoint = MarketChartLineRow & {
  readonly displayAt: string;
  readonly provenance: 'authoritative' | 'left-edge' | 'closed-gap';
};

export type MarketChartCandlePoint = MarketChartCandleRow & {
  readonly provenance: 'authoritative' | 'closed-gap';
};

export interface MarketChartCandlePresentation {
  readonly candles: readonly MarketChartCandlePoint[];
  readonly leftEdge: {
    readonly at: string;
    readonly value: string;
    readonly provenance: 'left-edge';
  } | null;
}

/** Presentation-only points. The input rows and canonical SDK cache remain sparse. */
export function materializeMarketChartLine(
  rows: readonly (MarketChartLineRow | MarketChartCandleRow)[],
  bounds: MarketChartPresentationBounds,
): readonly MarketChartLinePoint[] {
  const context = parseBounds(bounds);
  if (context === null) return [];
  const points: MarketChartLinePoint[] = [];
  let carry: string | null = null;
  let nextGapBucket = firstEligibleGapBucket(context.startMs, context.widthMs);

  if (bounds.leftEdgeValue !== undefined && context.startMs < context.endMs) {
    points.push({
      bucket: bounds.rangeStart,
      displayAt: bounds.rangeStart,
      value: bounds.leftEdgeValue,
      provenance: 'left-edge',
    });
    carry = bounds.leftEdgeValue;
  }

  for (const row of rows) {
    const bucketMs = Date.parse(row.bucket);
    if (!Number.isFinite(bucketMs) || bucketMs >= context.endMs) continue;
    if (carry !== null && bounds.gapPolicy === 'fill-closed') {
      appendLineGaps(points, nextGapBucket, bucketMs, carry, context);
    }
    const value = 'value' in row ? row.value : row.close;
    const sampleAt = 'sampleTs' in row ? row.sampleTs : undefined;
    const pointMs = sampleAt === undefined ? bucketMs : Date.parse(sampleAt);
    const displayMs = Math.max(pointMs, context.startMs);
    // A line cannot represent both the seed and an authoritative close at one X.
    if (
      points.length === 1 &&
      points[0]?.provenance === 'left-edge' &&
      displayMs === context.startMs
    ) {
      points.pop();
    }
    points.push({
      bucket: row.bucket,
      ...(sampleAt === undefined ? {} : { sampleTs: sampleAt }),
      displayAt: new Date(displayMs).toISOString(),
      value,
      provenance: 'authoritative',
    });
    carry = value;
    nextGapBucket = bucketMs + context.widthMs;
  }

  if (carry !== null && bounds.gapPolicy === 'fill-closed') {
    appendLineGaps(points, nextGapBucket, context.endMs, carry, context);
  }
  return points;
}

/** Flat OHLC presentation gaps are never written to the sparse candle cache. */
export function materializeMarketChartCandles(
  rows: readonly MarketChartCandleRow[],
  bounds: MarketChartPresentationBounds,
): MarketChartCandlePresentation {
  const context = parseBounds(bounds);
  if (context === null) return { candles: [], leftEdge: null };
  const candles: MarketChartCandlePoint[] = [];
  const leftEdge =
    bounds.leftEdgeValue !== undefined && context.startMs < context.endMs
      ? { at: bounds.rangeStart, value: bounds.leftEdgeValue, provenance: 'left-edge' as const }
      : null;
  let carry = leftEdge?.value ?? null;
  let nextGapBucket = firstEligibleGapBucket(context.startMs, context.widthMs);

  for (const row of rows) {
    const bucketMs = Date.parse(row.bucket);
    if (!Number.isFinite(bucketMs) || bucketMs >= context.endMs) continue;
    if (carry !== null && bounds.gapPolicy === 'fill-closed') {
      appendCandleGaps(candles, nextGapBucket, bucketMs, carry, context);
    }
    candles.push({ ...row, provenance: 'authoritative' });
    carry = row.close;
    nextGapBucket = bucketMs + context.widthMs;
  }

  if (carry !== null && bounds.gapPolicy === 'fill-closed') {
    appendCandleGaps(candles, nextGapBucket, context.endMs, carry, context);
  }
  return { candles, leftEdge };
}

interface ParsedBounds {
  readonly startMs: number;
  readonly endMs: number;
  readonly closedBeforeMs: number;
  readonly widthMs: number;
}

function parseBounds(bounds: MarketChartPresentationBounds): ParsedBounds | null {
  const startMs = Date.parse(bounds.rangeStart);
  const endMs = Date.parse(bounds.rangeEnd);
  const closedBeforeMs = Date.parse(bounds.closedBefore);
  const widthMs = bounds.bucketSeconds * 1000;
  if (
    !Number.isFinite(startMs) ||
    !Number.isFinite(endMs) ||
    !Number.isFinite(closedBeforeMs) ||
    !Number.isSafeInteger(widthMs) ||
    widthMs <= 0 ||
    endMs < startMs
  ) {
    return null;
  }
  return { startMs, endMs, closedBeforeMs, widthMs };
}

function firstEligibleGapBucket(startMs: number, widthMs: number): number {
  const originMs = widthMs === 604_800_000 ? 345_600_000 : 0;
  const bucketMs = Math.floor((startMs - originMs) / widthMs) * widthMs + originMs;
  return bucketMs + widthMs;
}

function appendLineGaps(
  points: MarketChartLinePoint[],
  firstBucket: number,
  until: number,
  value: string,
  bounds: ParsedBounds,
): void {
  for (
    let bucketMs = firstBucket;
    bucketMs < until &&
    bucketMs < bounds.endMs &&
    bucketMs + bounds.widthMs <= bounds.closedBeforeMs;
    bucketMs += bounds.widthMs
  ) {
    const bucket = new Date(bucketMs).toISOString();
    points.push({ bucket, displayAt: bucket, value, provenance: 'closed-gap' });
  }
}

function appendCandleGaps(
  candles: MarketChartCandlePoint[],
  firstBucket: number,
  until: number,
  close: string,
  bounds: ParsedBounds,
): void {
  for (
    let bucketMs = firstBucket;
    bucketMs < until &&
    bucketMs < bounds.endMs &&
    bucketMs + bounds.widthMs <= bounds.closedBeforeMs;
    bucketMs += bounds.widthMs
  ) {
    candles.push({
      bucket: new Date(bucketMs).toISOString(),
      open: close,
      high: close,
      low: close,
      close,
      provenance: 'closed-gap',
    });
  }
}
